import React, { useCallback, useEffect, useState } from 'react';
import ApiService from '../services/api';

/**
 * Product-wide feature flags (admin.testera.io).
 */
export default function AdminFeatureFlagsTab({ refreshTick: _refreshTick = 0 }) {
  const [flags, setFlags] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState('');
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    setError('');
    setLoading(true);
    try {
      const data = await ApiService.getAdminFeatureFlags();
      const nextFlags = data?.flags && typeof data.flags === 'object' ? data.flags : null;
      if (!nextFlags || !Object.keys(nextFlags).length) {
        // Never treat "empty" as all-off — server defaults require verification ON.
        setFlags({ emailVerificationRequired: true });
        setError('Flags response was empty; showing defaults (verification ON). Try Reload.');
      } else {
        setFlags(nextFlags);
      }
      setCatalog(Array.isArray(data?.catalog) ? data.catalog : []);
      setUpdatedAt(data?.updatedAt || null);
    } catch (e) {
      setError(e?.message || 'Failed to load feature flags');
      // Unknown state — do not imply verification is off
      setFlags({ emailVerificationRequired: true });
      setCatalog([{
        key: 'emailVerificationRequired',
        label: 'Require email verification',
        description: 'Could not load from server — showing default (ON). Fix connectivity, then Reload before toggling.',
        type: 'boolean',
      }]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load once on mount — do not re-fetch on Live refreshTick (avoids stacked 504s).
  useEffect(() => { load(); }, [load]);

  const toggle = async (key, next) => {
    setBusyKey(key);
    setMsg('');
    setError('');
    try {
      const data = await ApiService.updateAdminFeatureFlags({ [key]: next });
      setFlags(data?.flags && typeof data.flags === 'object' ? data.flags : {});
      setUpdatedAt(data?.updatedAt || new Date().toISOString());
      setMsg('Saved');
    } catch (e) {
      setError(e?.message || 'Failed to update flag');
    } finally {
      setBusyKey('');
    }
  };

  if (loading && !flags) {
    return <div className="text-sm text-slate-400">Loading feature flags…</div>;
  }

  const rows = catalog.length
    ? catalog
    : Object.keys(flags || {}).map((key) => ({ key, label: key, description: '', type: 'boolean' }));

  return (
    <div className="space-y-4 max-w-2xl">
      <div>
        <h2 className="text-sm font-semibold text-slate-100">Feature flags</h2>
        <p className="text-xs text-slate-500 mt-1">
          Product-wide toggles. Changes apply within a few seconds — no deploy needed.
        </p>
        {updatedAt && (
          <p className="text-[10px] text-slate-600 mt-1 tabular-nums">
            Last updated {new Date(updatedAt).toLocaleString()}
          </p>
        )}
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="mt-2 text-[11px] text-slate-400 hover:text-slate-200 underline underline-offset-2 disabled:opacity-50"
        >
          {loading ? 'Loading…' : 'Reload'}
        </button>
      </div>

      {error && <div className="text-xs text-amber-300">{error}</div>}
      {msg && <div className="text-xs text-emerald-300">{msg}</div>}

      <div className="app-panel divide-y divide-white/10">
        {rows.map((row) => {
          const on = !!(flags && flags[row.key]);
          const busy = busyKey === row.key;
          return (
            <div key={row.key} className="flex items-start justify-between gap-4 p-4">
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-200">{row.label || row.key}</div>
                {row.description ? (
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">{row.description}</p>
                ) : null}
                <p className="text-[10px] text-slate-600 mt-1 font-mono">{row.key}</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={on}
                disabled={busy || loading}
                onClick={() => toggle(row.key, !on)}
                className={`relative shrink-0 w-11 h-6 rounded-full transition-colors disabled:opacity-50 ${
                  on ? 'bg-primary' : 'bg-white/15'
                }`}
                title={on ? 'On — click to turn off' : 'Off — click to turn on'}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
                    on ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          );
        })}
        {rows.length === 0 && (
          <div className="p-4 text-xs text-slate-500">No flags configured.</div>
        )}
      </div>
    </div>
  );
}
